import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { App } from '@capacitor/app';
import { OrientationType, ScreenOrientation } from '@capawesome/capacitor-screen-orientation';
import { useLatest, useSize } from 'ahooks';
import { useLocation } from 'react-router';
import { Button, Dialog } from 'antd-mobile';
import lookie from 'lookie';
import { useSelector } from 'react-redux';
import { selectDevicePlatform, selectShouldAlertUpdateH5AfterAIConsultation } from '@/store/device';
import { ALL_PAGES, JUDGE_AS_VERTICAL_SCREEN_HEIGHT, SELECTED_CUSTOMER_ID } from '@/constant';
import { OrientationTipMask } from '@/components';
import { useNav } from '@/hooks';
import service from '@/service';
import {
  CurrentDimension,
  HeaderForVScreen,
  HomeTherapyRequests,
  Progress,
  SelectPart,
  TargetDimension,
  ResultPlan,
  MarkPainArea,
  PainConditionLevel,
  PhysicalCondition,
  PhysicalConditionLevel,
  TongueDiagnosis,
  PostureCondition,
  PostureConditionLevel,
  TransitionPage,
} from './components';
import { resetAIDiagnoseStore, setFormValue, useAIDiagnoseStore } from './store/useAIDiagnoseStore';
import { genStepsBySelectTherapyList } from './utils';
import TipsPage from './components/tips-page';

import cx from './index.module.scss';

interface Props {}

export type AIFormAction =
  // 首页-理疗诉求选择
  | 'homeTherapyRequests'
  // 个人信息
  | 'userInfo'
  // 选择部位
  | 'selectPart'
  // 填当前围度
  | 'currentDimension'
  // 填目标围度
  | 'targetDimension'
  // 希望改善的体质问题
  | 'physicalCondition'
  // 体质情况表单
  | 'physicalConditionLevel'
  // 舌诊
  | 'tongueDiagnosis'
  // 标注疼痛区域
  | 'markPainArea'
  // 疼痛情况
  | 'painConditionLevel'
  // 希望改善的体态问题
  | 'postureCondition'
  // 体态问题严重程度
  | 'postureConditionLevel'
  | 'transition-page';

const STEP_2_COMPONENTS: Record<AIFormAction, JSX.Element> = {
  homeTherapyRequests: <HomeTherapyRequests />,
  userInfo: <HomeTherapyRequests />,
  selectPart: <SelectPart />,
  currentDimension: <CurrentDimension />,
  targetDimension: <TargetDimension />,
  markPainArea: <MarkPainArea />,
  painConditionLevel: <PainConditionLevel />,
  physicalCondition: <PhysicalCondition />,
  physicalConditionLevel: <PhysicalConditionLevel />,
  tongueDiagnosis: <TongueDiagnosis />,
  postureCondition: <PostureCondition />,
  postureConditionLevel: <PostureConditionLevel />,
  'transition-page': <TransitionPage />,
};

const AIConsultation: React.FC<Props> = () => {
  const nav = useNav();
  const location = useLocation();
  const devicePlatform = useSelector(selectDevicePlatform);
  const shouldAlertUpdateH5AfterAIConsultation = useLatest(useSelector(selectShouldAlertUpdateH5AfterAIConsultation));
  const sizeRef = useRef(null);
  const screenSize = useSize(sizeRef);
  const isTipsPlayOK = useAIDiagnoseStore.use.isTipsPlayOK();
  const { selectTherapyType } = useAIDiagnoseStore.use.formValue();
  const prevBtnConfig = useAIDiagnoseStore.use.prevBtnConfig();
  const nextBtnConfig = useAIDiagnoseStore.use.nextBtnConfig();
  const currentStep = useAIDiagnoseStore.use.currentStep();

  const isVerticalScreen = useMemo(
    () => screenSize?.height > JUDGE_AS_VERTICAL_SCREEN_HEIGHT || screenSize?.height === 0,
    [screenSize?.height]
  );

  const steps = useMemo(() => genStepsBySelectTherapyList(selectTherapyType), [selectTherapyType]);

  /** 根据之前理疗记录还原减肥瘦身表单 */
  const restoreWeightLossForm = useCallback(async () => {
    const { data } = await service.customer.getCustomerProfile(lookie.get(SELECTED_CUSTOMER_ID));

    const storeDimension = data?.weightLossList?.reduce(
      (accur, cur) => {
        accur['currentDimension'][cur.problem] = cur.currentValue / 10;
        accur['targetDimension'][cur.problem] = cur.goalValue / 10;
        return accur;
      },
      {
        currentDimension: {},
        targetDimension: {},
      }
    );

    setFormValue(
      {
        currentDimension: storeDimension?.['currentDimension'] || {},
        targetDimension: storeDimension?.['targetDimension'] || {},
      },
      { isMerge: true }
    );
  }, []);

  useEffect(() => {
    restoreWeightLossForm();
    resetAIDiagnoseStore();
    useAIDiagnoseStore.persist?.clearStorage();
    return () => {
      resetAIDiagnoseStore();
      useAIDiagnoseStore.persist?.clearStorage();

      if (shouldAlertUpdateH5AfterAIConsultation.current) {
        setTimeout(() => {
          Dialog.alert({
            title: '检测到更新',
            content: (
              <div>
                <span>我们对产品进行了修复和优化，</span>
                <br />
                <span>请尽快完成更新</span>
              </div>
            ),
            confirmText: '我知道了',
            onConfirm() {
              App.exitApp().catch(err => {
                if (err.message === 'Not implemented on web.') {
                  window?.location?.reload();
                }
              });
            },
          });
        }, 80);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (devicePlatform !== 'android') return;

    setTimeout(() => ScreenOrientation.lock({ type: OrientationType.PORTRAIT_PRIMARY }), 0);
    return () => {
      ScreenOrientation.lock({ type: OrientationType.LANDSCAPE_PRIMARY });
    };
  }, [devicePlatform]);

  useEffect(() => {
    if (isVerticalScreen) {
      OrientationTipMask.hide();
    } else {
      OrientationTipMask.show();
    }
  }, [isVerticalScreen]);

  return (
    <div className={cx('AIConsultation')} ref={sizeRef}>
      <HeaderForVScreen
        backConfig={{
          text: '返回',
          action: () => {
            if (currentStep > steps?.length - 1) {
              if (location.state?.action === 'member-detail') {
                nav(ALL_PAGES.MEMBER_DETAIL);
              } else {
                nav(ALL_PAGES.HOME);
              }
            } else {
              Dialog.confirm({
                className: 'v-screen-dialog',
                title: '确认要退出AI问询吗？',
                content: '退出后，您输入的所有信息将不被记录哦',
                confirmText: '确认',
                onConfirm: async () => {
                  if (location.state?.action === 'member-detail') {
                    nav(ALL_PAGES.MEMBER_DETAIL);
                  } else {
                    nav(ALL_PAGES.HOME);
                  }
                },
              });
            }
          },
        }}
        title="AI问询"
        showBorder={false}
      />

      {isTipsPlayOK ? (
        currentStep > steps?.length - 1 ? (
          <ResultPlan />
        ) : (
          <>
            {steps[currentStep] !== 'transition-page' && <Progress />}
            <div className={cx('content-wrapper')}>
              {/* 根据步骤渲染对于组件 */}
              {currentStep < 0 ? <HomeTherapyRequests /> : STEP_2_COMPONENTS[steps[currentStep]]}
            </div>
            <div className={cx('btn-operater')}>
              {prevBtnConfig?.show && (
                <Button
                  color="primary"
                  disabled={prevBtnConfig?.disable}
                  block
                  className={cx('btn', 'plain')}
                  onClick={prevBtnConfig?.action}
                >
                  {prevBtnConfig?.text || '上一步'}
                </Button>
              )}
              <Button
                color="primary"
                block
                loading={nextBtnConfig?.loading}
                disabled={nextBtnConfig?.disable}
                className={cx('btn', { large: !prevBtnConfig?.show })}
                onClick={nextBtnConfig.action}
              >
                {nextBtnConfig?.text || '下一步'}
              </Button>
            </div>
          </>
        )
      ) : (
        <TipsPage />
      )}
    </div>
  );
};

export default AIConsultation;
